/* eslint-disable @typescript-eslint/no-unused-expressions */
import { Platform } from 'react-native';
import { AnalyticsClient, AnalyticsConfig } from '@24i/nxg-sdk-photon';
import { ANALYTICS_TRIGGERS } from '@24i/nxg-sdk-smartott-shared/src/analytics/constants';
import { AnyPayload, EventBody } from '@24i/nxg-sdk-photon/src/clients/AnalyticsClient';
import { getAdvertisingInfo } from '@24i/nxg-sdk-smartott-shared/src/hooks/usePlayerSourceManager/advertisingInfo';
import axios from 'axios';
import {
    isEventScheduled,
    detectAllowTracking,
} from '@24i/nxg-sdk-smartott-shared/src/analytics/utils';
import {
    TWENTY_FOUR_IQ_TRIGGERS,
    TRIGGER_NAME_TO_24IQ_EVENTS,
    EVENTS,
    REDUNDANT_EVENTS,
} from './constants';
import { mapSceneView } from './mappers/mapSceneView';
import { mapSearch } from './mappers/mapSearch';
import { mapBuffering } from './mappers/mapBuffering';
import { mapScroll } from './mappers/mapScroll';
import { mapVideoProgress } from './mappers/mapVideoProgress';
import { mapVideoStop } from './mappers/mapVideoStop';
import { mapVideoComplete } from './mappers/mapVideoComplete';
import { mapPlayerOpen } from './mappers/mapPlayerOpen';
import { mapPlayerClose } from './mappers/mapPlayerClose';
import { mapVideoStart } from './mappers/mapVideoStart';
import { mapVideoPause } from './mappers/mapVideoPause';
import { mapAdEvent } from './mappers/mapAdEvent';
import { EventMappingFunction } from './types';

const eventsMapping: Record<EVENTS, EventMappingFunction> = {
    [EVENTS.SCENE_VIEW]: mapSceneView,
    [EVENTS.SEARCH]: mapSearch,
    [EVENTS.BUFFERING]: mapBuffering,
    [EVENTS.SCROLL]: mapScroll,
    [EVENTS.PLAYER_OPEN]: mapPlayerOpen,
    [EVENTS.PLAYER_CLOSE]: mapPlayerClose,
    [EVENTS.VIDEO_START]: mapVideoStart,
    [EVENTS.VIDEO_STOP]: mapVideoStop,
    [EVENTS.VIDEO_PROGRES]: mapVideoProgress,
    [EVENTS.VIDEO_COMPLETE]: mapVideoComplete,
    [EVENTS.VIDEO_PAUSE]: mapVideoPause,
    [EVENTS.AD_START]: mapAdEvent,
    [EVENTS.AD_SKIPPED]: mapAdEvent,
    [EVENTS.AD_BUFFERING]: mapAdEvent,
    [EVENTS.AD_STOP]: mapAdEvent,
    [EVENTS.AD_ERROR]: mapAdEvent,
    [EVENTS.AD_COMPLETE]: mapAdEvent,
};

export class TwentyFourIQClient implements AnalyticsClient {
    name = '24iQ';

    triggers: string[];

    baseURL = '';

    trackingAllowed = false;

    constructor() {
        this.triggers = TWENTY_FOUR_IQ_TRIGGERS;
    }

    async init(config: AnalyticsConfig): Promise<void> {
        this.baseURL = config.baseURL;
    }

    async sendEvent(eventBody: EventBody) {
        try {
            await axios.post(this.baseURL, eventBody, {
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json;charset=UTF-8',
                },
            });
        } catch (error) {
            console.error('24iQ Analytics err:', error);
        }
    }

    // Adds async-derived properties to the payload for use in mapping functions
    async getAdditionalProperties(properties: AnyPayload): Promise<AnyPayload> {
        const updatedPayload = {
            ...properties.payload,
            isTrackingAllowed: await detectAllowTracking(), // this flag will be used to track user email info
            isAdTargetingAllowed: await this.isAdTargettingAllowed(), // ad targetting
            attributionId: this.processAttributionId(properties.payload),
        };

        return {
            ...properties,
            payload: updatedPayload,
        };
    }

    async onEvent(triggerName: ANALYTICS_TRIGGERS, payload: AnyPayload): Promise<void> {
        const events = TRIGGER_NAME_TO_24IQ_EVENTS[triggerName];
        const params = await this.getAdditionalProperties(payload);
        const eventNameFromPayload = params.payload.event;
        if (!isEventScheduled(REDUNDANT_EVENTS, eventNameFromPayload)) return;
        events &&
            (await Promise.all(
                events.map(async (event: string) => {
                    const mappingFunction = eventsMapping[event];
                    const eventBody = mappingFunction && mappingFunction(triggerName, params);
                    eventBody && (await this.sendEvent(eventBody));
                })
            ));
    }

    async isAdTargettingAllowed(): Promise<boolean> {
        const advertisingInfo = await getAdvertisingInfo();

        // for web send inverted value of dnt
        if (Platform.OS === 'web') {
            if (advertisingInfo.doNotTrack === null || advertisingInfo.doNotTrack) {
                return false;
            }
            return true;
        }

        // If isAdTrackingLimited is null or true (limited), return false
        if (advertisingInfo.isAdTrackingLimited === null || advertisingInfo.isAdTrackingLimited) {
            return false;
        }

        // If isAdTrackingLimited is false (not limited), return true
        return true;
    }

    // Function to evaluate and eleminate redundant attribution IDs from the payload
    // If the assetId of current attribution ID, doesn't match with the current assetId, then remove it from the payload
    processAttributionId(payload): any {
        const attributionId = payload?.attributionId;
        if (payload?.assetId !== undefined && attributionId) {
            const assetId = payload?.asset?.id || payload?.params?.id;
            if (assetId !== payload?.assetId) {
                return null;
            }
        }
        return attributionId;
    }
}

export const createTwentyFourIQClient = () => new TwentyFourIQClient();
